/* eslint-disable max-len */
import axios from 'axios';
import config from '../config/index.js';
import { authHeader } from '../redux/helper.jsx';

// Inject seller_email for super-admin impersonation on every request
// Backend resolve_seller_email checks queryStringParameters first, so query params work for all methods
axios.interceptors.request.use(requestConfig => {
    const sellerEmail = sessionStorage.getItem('selectedSellerEmail');
    if (!sellerEmail) return requestConfig;

    // Don't inject seller_email into super-admin session lock API calls
    if (requestConfig.url?.includes('/super-admin/session')) return requestConfig;

    requestConfig.params = { ...requestConfig.params, seller_email: sellerEmail };
    return requestConfig;
});

// Shared request helpers: each owns the axios call and its try/catch, returning a
// consistent { error: false, data } on success or { error: true, message, status, data }
// on failure. `message` intentionally preserves today's dominant shape (the whole axios
// error object) so existing callers reading `.message.status`/`.message.data.message` keep
// working; `status`/`data` are added as a non-breaking superset for callers that want them
// directly (e.g. the super-admin session lock flow).
async function handle(axiosCallFactory) {
    try {
        const response = await axiosCallFactory();
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e, status: e.response?.status, data: e.response?.data };
    }
}

async function get(url, requestConfig = {}) {
    return handle(() => axios.get(url, requestConfig));
}
async function post(url, body, requestConfig = {}) {
    return handle(() => axios.post(url, body, requestConfig));
}
async function patch(url, body, requestConfig = {}) {
    return handle(() => axios.patch(url, body, requestConfig));
}
async function put(url, body, requestConfig = {}) {
    return handle(() => axios.put(url, body, requestConfig));
}
async function del(url, requestConfig = {}) {
    return handle(() => axios.delete(url, requestConfig));
}

/**
 * The function VerifyRecaptcha is an asynchronous function that sends a POST request to verify a
 * reCAPTCHA token and returns an object with an error flag and the response data.
 * @param token - The `token` parameter is a string that represents the user's response to the
 * reCAPTCHA challenge. It is typically generated by the reCAPTCHA widget on the client-side and passed
 * to the server for verification.
 * @returns an object with two properties: "error" and "data". The "error" property indicates whether
 * an error occurred during the execution of the function. If an error occurred, the value of "error"
 * will be true. The "data" property contains the response data from the API call.
 */
async function VerifyRecaptcha(token) {
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/verify-captcha`, token)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

/**
 * The function `VerifyOtp` is an asynchronous function that sends a POST request to a specified API
 * endpoint for OTP validation and returns the response data or an error object.
 * @param body - The `body` parameter is an object that contains the data to be sent in the request
 * body when making a POST request to the OTP validation endpoint. It should include the necessary
 * information required for OTP validation, such as the user's phone number and the OTP code.
 * @returns The function `VerifyOtp` is returning an object with two properties: `error` and `data`.
 * The `error` property indicates whether an error occurred during the execution of the function. If
 * `error` is `false`, it means that the OTP validation was successful and the `data` property contains
 * the response data from the API call. If `error` is `true`, it means
 */
async function VerifyOtp(body) {
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/otp-validation`, body)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function ViewProfile(body) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/users-management/${body.email}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function UpdateProfile(body) {
    const headers= await authHeader();
    try{
        const data = await axios.patch(`${config.API.baseURL}/users-management/${body.email_address}`,body,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function ResetPasswordLink(body){
    const response = await post(`${config.API.baseURL}/users-management/forgot_password`, body);
    if (response.error) return { error: true, message: response.message };
    return { error: false, data: response.data };
}
async function ConfirmPassword(body){
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/reset_password`, body)
        return { error: false, data: data.data }
    }
    catch(error){
        return { error: true, data: error.response };
    }
}
async function UpdatePassword(email_address, body) {
    const headers= await authHeader();
    try{
        const data = await axios.patch(`${config.API.baseURL}/users-management/password-update/${email_address}`, body, {
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function CreatePassword( body ) {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/set-password`, body, {
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function getKycToken() {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/users-management/generate`, { headers });
}
async function getKybToken() {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/users-management/kyb-generate`, { headers });
}
async function updatePricing(email_address,body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/users-management/update-plan/${email_address}`, body, { headers });
}
async function connectStripe() {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/users-management/stripe`, { headers });
}
async function disconnectStripe(body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/users-management/stripe`, body, { headers });
}

async function connectPaypal() {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/paypal/paypal-connect`, { headers });
}

async function disconnectPaypal(body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/paypal/paypal-disconnect`, body, { headers });
}

async function AuctionList(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/auctions${endpoint}`, { headers });
}
async function AddNotes(endpoint,body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/auctions${endpoint}`, body, { headers });
}

async function CreateAuction(body) {
    const headers= await authHeader();
    return post(`${config.API.baseURL}/auctions`, body, { headers });
}

async function CheckSlugAvailability(slug, auctionId = '') {
    const headers = await authHeader();
    const response = await get(
        `${config.API.baseURL}/auctions/check-slug?vanity_slug=${encodeURIComponent(slug)}${auctionId ? `&auction_id=${auctionId}` : ''}`,
        { headers },
    );
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function LotList(endpoint, live=false) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/${live ? 'in-person-auction/list-lots' : 'auctions/lots'}${endpoint}`, { headers });
}
async function viewAuction(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/auctions/view${endpoint}`, { headers });
}
async function editAuction(id,body) {
    const headers= await authHeader();
    const response = await patch(`${config.API.baseURL}/auctions/update/${id}`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function CreateLot(body, live=false) {
    const headers= await authHeader();
    return post(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`, body, { headers });
}
async function ImportLot(body) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/import`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function deleteLot(body, live=false) {
    const headers= await authHeader();
    const response = await del(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`, { headers, data: body });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function updateLot(body, live=false) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`, body, { headers });
}

async function CloneAuction(body) {
    const headers= await authHeader();
    return post(`${config.API.baseURL}/auctions/clone`, body, { headers });
}

async function DeleteAuction(id, live=false) {
    const headers= await authHeader();
    const response = await del(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/${id}`, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}
async function publishAuction(id) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/auctions/update/${id}?published=true`, {}, { headers });
}

async function UnpublishAuction(auction_id,body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/auctions/${auction_id}`, body, { headers });
}

async function reorderLots(body) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/reorder-lots`, body, {
            headers: headers
        });
        return { error: false, data: response.data };
    } catch (e) {
        console.error('Error reordering lots:', e);
        return { error: true, message: e.response?.data?.message || 'Failed to reorder lots' };
    }
}

// Keep the existing single lot reorder method as backup
async function reorderLot(body) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/reorder`, body, {
            headers: headers
        });

        if (response.status === 204) {
            return { error: false, data: {} };
        }
        return { error: false, data: response.data };
    } catch (e) {
        console.error('Error reordering lot:', e);
        return { error: true, message: e.response?.data?.message || 'Failed to reorder lot' };
    }
}

async function BidderList(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/bids${endpoint}`, { headers });
}
async function handleBidders(endpoint) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/buyers/approval${endpoint}`, {}, { headers });
}

async function SubDomainSetup(endpoint,body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/subdomain/subdomain${endpoint}`, body, { headers });
}

async function BidderDetails(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/bids/${endpoint}`, { headers });
}
async function SalesList(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/orders/seller${endpoint}`, { headers });
}

async function getOrderDetails(id) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/orders/sales?order_id=${id}`, { headers });
}

async function getAllBidsOfLot(id,endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/lot-bid-history/${id}?${endpoint}`, { headers });
}

async function AllBidderList(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/seller-bidder-management${endpoint}`, { headers });
}

async function AllSalesList(endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/seller-bidder-management/seller-orders${endpoint}`, { headers });
}

async function getAllBids(id,endpoint) {
    const headers= await authHeader();
    // eslint-disable-next-line max-len
    return get(`${config.API.baseURL}/lot-bid-history/seller/bids?auction_id=${id}&${endpoint}`, { headers });
}

async function changePaymentInfo(orderId, {paymentType, paymentStatus}) {
    const headers = await authHeader();
    try {
        const response = await axios.put(`${config.API.baseURL}/orders/update`, {
            order_id: orderId,
            payment_status: paymentStatus,
            payment_type: paymentType
        }, {
            headers
        })
        return { error: false, res: response }
    } catch (e) {
        return { error: true, message: e }
    }
}

async function sendPaymentReminder(orderId, emailAddress = null) {
    const headers = await authHeader();
    const body = {
        order_id: orderId
    };
    if (emailAddress) {
        body.email_address = emailAddress;
    }
    const response = await post(`${config.API.baseURL}/orders/send-payment-reminder`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function BannerNotification() {
    return get(`${config.API.baseURL}/site-banner/${encodeURIComponent("All Sellers")}`);
}

async function NewsNotication(body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/newsletter`, body, { headers });
}


async function Accesslogs() {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/save-log`,{},{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function getQuickSightEmbedUrl() {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/quicksight-dashboards`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function AuctionInsights(auction_id) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/quicksight-dashboards/auction-view?auction_id=${auction_id}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function GetTemplate(name, default_name) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/users-management/get-template/${name}?default_template=${default_name}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function updateTemplate(action, body) {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/create-template?action=${action}`,body,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function getVersion(body) {
    try{
        const data = await axios.post(`${process.env.REACT_APP_VERSION_API_BASE_URL}`,body)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function CreateLiveAuction(body) {
    const headers= await authHeader();
    return post(`${config.API.baseURL}/in-person-auction/`, body, { headers });
}

async function EditLiveAuction(auction_id,body) {
    const headers= await authHeader();
    const response = await patch(`${config.API.baseURL}/in-person-auction/${auction_id}`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function PublishLiveAuction(body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/in-person-auction/publish`, body, { headers });
}

async function UnpublishLiveAuction(body) {
    const headers= await authHeader();
    return patch(`${config.API.baseURL}/in-person-auction/unpublish`, body, { headers });
}

async function CompleteAuction(body) {
    const headers= await authHeader();
    const response = await patch(`${config.API.baseURL}/in-person-auction/auction-complete`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function GetLeaderboard(auction_id) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/auctions/leaderboard/${auction_id}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function GetLiveAuctionBids(auction_id, bid_type, endpoint) {
    const headers= await authHeader();
    return get(`${config.API.baseURL}/in-person-auction/bids?auction_id=${auction_id}&bid_type=${bid_type}&${endpoint}`, { headers });
}

async function deleteBid(bid_id) {
    const headers= await authHeader();
    const response = await del(`${config.API.baseURL}/in-person-auction/delete-bid/${bid_id}`, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function CancelAuction(body) {
    const headers= await authHeader();
    const response = await patch(`${config.API.baseURL}/in-person-auction/cancel-auction`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function AuctionStats(body) {
    const headers= await authHeader();
    const response = await get(`${config.API.baseURL}/in-person-auction/auction-statistics?auction_id=${body.auction_id}`, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function deleteImageFromS3(body) {
    const headers= await authHeader();
    const response = await del(`${config.API.baseURL}/auctions/image/`, { headers, data: body });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

// Super-admin session lock APIs
async function acquireSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    const response = await post(`${config.API.baseURL}/users-management/super-admin/session`, { seller_email: sellerEmail }, { headers });
    if (response.error) return { error: true, status: response.status, data: response.data };
    return { error: false, data: response.data };
}

async function releaseSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    const response = await del(`${config.API.baseURL}/users-management/super-admin/session`, {
        headers,
        params: { seller_email: sellerEmail },
    });
    if (response.error) return { error: true, data: response.data };
    return { error: false, data: response.data };
}

async function getSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    const params = sellerEmail ? { seller_email: sellerEmail } : {};
    const response = await get(`${config.API.baseURL}/users-management/super-admin/session`, { headers, params });
    if (response.error) return { error: true, data: response.data };
    return { error: false, data: response.data };
}

async function ViewLot(auction_id, lot_number) {
    const headers= await authHeader();
    const response = await get(`${config.API.baseURL}/auctions/lots/${auction_id}/${lot_number}`, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function processPayment(orderId) {
    const headers = await authHeader();
    const body = {
        order_id: orderId
    };
    const response = await post(`${config.API.baseURL}/payments/process-stored-card`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

async function sendPaymentRequest(body) {
    const headers = await authHeader();
    const response = await post(`${config.API.baseURL}/orders/send-payment-request`, body, { headers });
    if (response.error) return { error: true, message: response.message?.response };
    return { error: false, data: response.data };
}

export const dataService = {
    VerifyRecaptcha,
    VerifyOtp,
    ViewProfile,
    UpdateProfile,
    ResetPasswordLink,
    ConfirmPassword,
    UpdatePassword,
    CreatePassword,
    getKycToken,
    updatePricing,
    getKybToken,
    connectStripe,
    disconnectStripe,
    AuctionList,
    AddNotes,
    CreateAuction,
    CheckSlugAvailability,
    LotList,
    CreateLot,
    reorderLot,
    reorderLots,
    viewAuction,
    ImportLot,
    deleteLot,
    updateLot,
    editAuction,
    CloneAuction,
    DeleteAuction,
    publishAuction,
    BidderList,
    handleBidders,
    SubDomainSetup,
    BidderDetails,
    SalesList,
    getOrderDetails,
    getAllBidsOfLot,
    getAllBids,
    AllBidderList,
    UnpublishAuction,
    changePaymentInfo,
    sendPaymentReminder,
    AllSalesList,
    BannerNotification,
    NewsNotication,
    connectPaypal,
    disconnectPaypal,
    Accesslogs,
    getQuickSightEmbedUrl,
    AuctionInsights,
    GetTemplate,
    updateTemplate,
    getVersion,
    CreateLiveAuction,
    EditLiveAuction,
    PublishLiveAuction,
    UnpublishLiveAuction,
    CompleteAuction,
    GetLeaderboard,
    GetLiveAuctionBids,
    deleteBid,
    CancelAuction,
    AuctionStats,
    deleteImageFromS3,
    ViewLot,
    processPayment,
    sendPaymentRequest,
    acquireSuperAdminSession,
    releaseSuperAdminSession,
    getSuperAdminSession
}
